import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  useEmailTemplate,
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  usePreviewEmailTemplate,
} from '../../api/email';
import type { CreateTemplateDto, UpdateTemplateDto } from '../../types/email';

export function EmailTemplateEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const { data: templateData, isLoading: loadingTemplate } = useEmailTemplate(isNew ? '' : id!);
  const createMutation = useCreateEmailTemplate();
  const updateMutation = useUpdateEmailTemplate();
  const previewMutation = usePreviewEmailTemplate();

  const [formData, setFormData] = useState<CreateTemplateDto>({
    name: '',
    description: '',
    subject: '',
    body: '',
    bodyText: '',
    isActive: true,
  });
  const [previewData, setPreviewData] = useState<Record<string, string>>({});
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isSystemTemplate = !isNew && templateData?.data?.isSystem;

  useEffect(() => {
    if (templateData?.data && !isNew) {
      const template = templateData.data;
      setFormData({
        name: template.name,
        description: template.description || '',
        subject: template.subject,
        body: template.body,
        bodyText: template.bodyText || '',
        isActive: template.isActive,
      });
    }
  }, [templateData, isNew]);

  const handleChange = (field: keyof CreateTemplateDto, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handlePreviewDataChange = (key: string, value: string) => {
    setPreviewData((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Betreff ist erforderlich';
    }
    if (!formData.body.trim()) {
      newErrors.body = 'Inhalt ist erforderlich';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (isNew) {
        await createMutation.mutateAsync(formData);
        toast.success('Vorlage erfolgreich erstellt');
      } else {
        await updateMutation.mutateAsync({
          id: id!,
          data: formData as UpdateTemplateDto,
        });
        toast.success('Vorlage erfolgreich gespeichert');
      }
      navigate('/email/templates');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
      toast.error(`Fehler beim Speichern: ${message}`);
    }
  };

  const handlePreview = async () => {
    if (!id || isNew) {
      toast.warning('Speichern Sie die Vorlage zuerst, um eine Vorschau zu sehen.');
      return;
    }
    try {
      const result = await previewMutation.mutateAsync({
        id,
        data: previewData,
      });
      setPreviewHtml(result.data.html);
      setShowPreview(true);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error('Fehler bei der Vorschau');
    }
  };

  // Extract variables from template using regex
  const extractVariables = (text: string): string[] => {
    const matches = text.match(/\{\{([^}]+)\}\}/g) || [];
    const vars = matches.map((m) => m.replace(/\{\{|\}\}/g, '').trim());
    return [...new Set(vars)];
  };

  const templateVariables = [
    ...extractVariables(formData.subject),
    ...extractVariables(formData.body),
  ].filter((v, i, arr) => arr.indexOf(v) === i);

  if (loadingTemplate && !isNew) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-sm text-gray-500">Laden...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/email/templates')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Zurueck zu Vorlagen
        </button>
        <div className="flex items-center space-x-3">
          <h1 className="text-2xl font-bold text-gray-900">
            {isNew ? 'Neue Vorlage erstellen' : 'Vorlage bearbeiten'}
          </h1>
          {isSystemTemplate && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              System
            </span>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Grunddaten</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Vorlagenname *
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="z.B. bewerber-welcome"
                disabled={isSystemTemplate}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  isSystemTemplate
                    ? 'bg-gray-100 cursor-not-allowed border-gray-300'
                    : errors.name
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              <p className="mt-1 text-xs text-gray-500">
                {isSystemTemplate
                  ? 'System-Vorlagen: Name kann nicht geaendert werden'
                  : 'Eindeutiger technischer Name (ohne Leerzeichen)'}
              </p>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Beschreibung
              </label>
              <input
                type="text"
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Kurze Beschreibung des Verwendungszwecks"
                disabled={isSystemTemplate}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  isSystemTemplate
                    ? 'bg-gray-100 cursor-not-allowed border-gray-300'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {isSystemTemplate && (
                <p className="mt-1 text-xs text-gray-500">
                  System-Vorlagen: Beschreibung kann nicht geaendert werden
                </p>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-gray-700">Vorlage ist aktiv</span>
              </label>
            </div>
          </div>
        </div>

        {/* Template Content */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Vorlageninhalt</h2>
          <p className="text-sm text-gray-500 mb-4">
            Verwenden Sie Handlebars-Syntax fuer Variablen: {'{{'}variableName{'}}'}
          </p>

          <div className="space-y-4">
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                Betreff *
              </label>
              <input
                type="text"
                id="subject"
                value={formData.subject}
                onChange={(e) => handleChange('subject', e.target.value)}
                placeholder="z.B. Willkommen bei JunivaCRM, {{firstName}}!"
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                  errors.subject
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
            </div>

            <div>
              <label htmlFor="body" className="block text-sm font-medium text-gray-700">
                HTML-Inhalt *
              </label>
              <textarea
                id="body"
                rows={12}
                value={formData.body}
                onChange={(e) => handleChange('body', e.target.value)}
                placeholder={`<h1>Hallo {{firstName}}!</h1>
<p>Willkommen bei JunivaCRM.</p>
<p>Mit freundlichen Gruessen,<br>Ihr Juniva Team</p>`}
                className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm font-mono ${
                  errors.body
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                }`}
              />
              {errors.body && <p className="mt-1 text-sm text-red-600">{errors.body}</p>}
            </div>

            <div>
              <label htmlFor="bodyText" className="block text-sm font-medium text-gray-700">
                Text-Version (optional)
              </label>
              <textarea
                id="bodyText"
                rows={6}
                value={formData.bodyText}
                onChange={(e) => handleChange('bodyText', e.target.value)}
                placeholder="Hallo {{firstName}}! Willkommen bei JunivaCRM..."
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm font-mono"
              />
              <p className="mt-1 text-xs text-gray-500">
                Plain-Text Version fuer E-Mail Clients ohne HTML-Unterstuetzung
              </p>
            </div>
          </div>
        </div>

        {/* Variables Preview */}
        {templateVariables.length > 0 && !isNew && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Vorschau mit Testdaten</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mb-4">
              {templateVariables.map((variable) => (
                <div key={variable}>
                  <label className="block text-sm font-medium text-gray-700">{variable}</label>
                  <input
                    type="text"
                    value={previewData[variable] || ''}
                    onChange={(e) => handlePreviewDataChange(variable, e.target.value)}
                    placeholder={`Wert fuer ${variable}`}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={handlePreview}
              disabled={previewMutation.isPending}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {previewMutation.isPending ? 'Laden...' : 'Vorschau anzeigen'}
            </button>
          </div>
        )}

        {/* Preview Modal */}
        {showPreview && previewHtml && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75"
                onClick={() => setShowPreview(false)}
              />
              <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                  <h3 className="text-lg font-medium text-gray-900">E-Mail Vorschau</h3>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div
                  className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/email/templates')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {(createMutation.isPending || updateMutation.isPending) && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            )}
            {isNew ? 'Erstellen' : 'Speichern'}
          </button>
        </div>
      </form>
    </div>
  );
}
